import React, { useCallback, useMemo } from 'react';
import { Menu } from '@ui/menus/Menu';
import { getActivityTypeLabel } from '@shared/helpers/design/activities.helpers';
import { ActivityColorDot } from '../../activities/ActivityColorDot';
import { useAddActivityRefMenuRequest } from './AddActivityRefMenuContext';
import { useDesignActivities } from '@features/design/hooks/activities.hooks';
import {
  ActivityType,
  programActivityTypes,
} from '@shared/models/design/activities';
import { getAvailableActivities } from '@shared/helpers/design/activity-ref.helpers';

/**
 * Singleton-meny för att välja {@link Activity} att lägga till som activity refs.
 * Öppnas via {@link useActivityRefMenu} och delar instans via context, på samma sätt
 * som {@link StoreySettingsPanel}.
 */
export const AddActivityRefMenu: React.FC = () => {
  const { request, onSelect, onClose } = useAddActivityRefMenuRequest();
  const storey = request?.storey;

  // Activities that exist in project
  const existingActivities = useDesignActivities();

  // Activity types not added to project
  const missingActivityTypes = useMemo(() => {
    return programActivityTypes.filter(
      (type) => !existingActivities.some((activity) => activity.type === type),
    );
  }, [existingActivities]);

  const addFromExistingActivities = useMemo(() => {
    return storey ? getAvailableActivities(storey, existingActivities) : [];
  }, [existingActivities, storey]);

  /**
   * Resolve the selection before the menu onClose handler runs.
   * @param type {@link ActivityType} selected in the menu
   */
  const handleSelectMouseDown = useCallback(
    (type: ActivityType) => (event: React.MouseEvent<HTMLElement>) => {
      // Resolve selection early so closeAllMenus does not clear the resolver.
      event.preventDefault();
      event.stopPropagation();
      onSelect(type);
    },
    [onSelect],
  );

  if (!request || !storey) return null;

  return (
    <Menu
      anchorEl={request.anchorEl}
      anchorPosition={request.anchorPosition}
      open
      onClose={onClose}
    >
      {addFromExistingActivities.length > 0 && (
        <Menu.Group label="Use existing">
          {addFromExistingActivities.map((activity) => (
            <Menu.Item
              key={activity.id}
              icon={<ActivityColorDot type={activity.type} />}
              onMouseDown={handleSelectMouseDown(activity.type)}
            >
              {getActivityTypeLabel(activity)}
            </Menu.Item>
          ))}
        </Menu.Group>
      )}
      {missingActivityTypes.length > 0 && (
        <Menu.Group label="Add new">
          {missingActivityTypes.map((type) => (
            <Menu.Item
              key={type}
              icon={<ActivityColorDot type={type} />}
              onMouseDown={handleSelectMouseDown(type)}
            >
              {getActivityTypeLabel(type)}
            </Menu.Item>
          ))}
        </Menu.Group>
      )}
    </Menu>
  );
};
